'use client';

import { useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgInfo } from '@/components/icons-eataly';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateForLocale } from '@/lib/date-utils';
import type { OmnibusPriceDisplay } from '@/lib/omnibus';
import { cn } from '@/lib/utils';

function isTouchEvent(event: PointerEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) {
    if ('pointerType' in event && event.pointerType === 'touch') return true;
    return 'pointerType' in event.nativeEvent && event.nativeEvent.pointerType === 'touch';
}

export interface OmnibusPriceIndicationProps {
    /** `omnibus` value of `useProductPriceWeightDisplay`; nothing is rendered when null. */
    indication: OmnibusPriceDisplay | null | undefined;
    className?: string;
    invertTextColor?: boolean;
}

/** Legal explanation for the struck previous price. Hover on desktop, tap to toggle on touch. */
export function OmnibusPriceTooltipTrigger({ className }: { className?: string }) {
    const { t } = useTranslation('product');
    const [tooltipOpen, setTooltipOpen] = useState(false);
    // The click that follows a touch often has no pointerType, but Radix still closes on it.
    const touchGestureRef = useRef(false);

    return (
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'pointer-events-auto relative z-20 inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full',
                        className
                    )}
                    aria-label={t('price.omnibus.tooltipAriaLabel')}
                    data-testid="omnibus-tooltip-trigger"
                    onPointerDown={(event) => {
                        if (!isTouchEvent(event)) {
                            touchGestureRef.current = false;
                            return;
                        }
                        // Skip Radix's pointerdown, which closes an open tooltip and
                        // blocks the focus-open. Toggle here so a touch can reopen it.
                        event.preventDefault();
                        touchGestureRef.current = true;
                        setTooltipOpen((open) => !open);
                    }}
                    onClick={(event) => {
                        const touchClick = touchGestureRef.current || isTouchEvent(event);
                        touchGestureRef.current = false;
                        if (!touchClick) return;
                        // Radix closes on click. On touch that is the same gesture that
                        // just opened the tooltip, so it only flashes and then stays shut.
                        event.preventDefault();
                        event.stopPropagation();
                    }}>
                    <SvgInfo className="size-4" aria-hidden />
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6} className="max-w-xs px-3 py-2 text-left">
                {t('price.omnibus.tooltip')}
            </TooltipContent>
        </Tooltip>
    );
}

/**
 * EU Omnibus directive price disclosure (EATWS-472): the "Prezzo precedente" label with its legal
 * tooltip, the struck-through previous total and per-unit prices (lowest price of the 30 days before
 * the promotion started) and the promotion period. The data comes from the `c_priorBestPrice` /
 * `c_priceValid*` attributes injected by the SCAPI hooks (`omnibusEnabled` site preference), so
 * nothing renders when the feature is off or the product is not reduced.
 */
export function OmnibusPriceIndication({ indication, className, invertTextColor }: OmnibusPriceIndicationProps) {
    const { t, i18n } = useTranslation('product');
    if (!indication) return null;

    const locale = i18n.language;
    const from = formatDateForLocale(indication.validFrom ?? undefined, locale);
    const to = formatDateForLocale(indication.validTo ?? undefined, locale);
    const validity =
        from && to
            ? t('price.omnibus.validityRange', { from, to })
            : from
              ? t('price.omnibus.validityFrom', { from })
              : to
                ? t('price.omnibus.validityTo', { to })
                : null;

    if (!indication.priorFormatted && !validity) return null;

    const primaryColor = invertTextColor ? 'text-neutral-white' : 'text-neutral-10';
    const secondaryColor = invertTextColor ? 'text-neutral-gray-70' : 'text-neutral-40';

    return (
        <div
            className={cn('flex w-full min-w-0 flex-col gap-0.5 body-copy-s', primaryColor, className)}
            data-testid="omnibus-price-indication">
            {indication.priorFormatted ? (
                <div className="flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
                    <span>{t('price.omnibus.previousPriceLabel')}</span>
                    <s className="body-copy-s-bold line-through" data-testid="omnibus-previous-price">
                        {indication.priorFormatted}
                    </s>
                    {indication.priorPerUnitLine ? (
                        <s className={secondaryColor} data-testid="omnibus-previous-unit-price">
                            {indication.priorPerUnitLine}
                        </s>
                    ) : null}
                    <OmnibusPriceTooltipTrigger />
                </div>
            ) : null}
            {validity ? (
                <p className={cn('m-0', secondaryColor)} data-testid="omnibus-validity">
                    {validity}
                </p>
            ) : null}
        </div>
    );
}
